import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { classNames } from '@/utils/classnames';
import type { TokenInfo } from '@/utils/token.types';

import styles from './styles.module.css';

const STROKE_COLOR = 'var(--color-special-xchain-stroke)';

const TokenIconElement = ({
  address,
  chainId,
  left = false,
}: {
  address: string;
  chainId: number;
  left?: boolean;
}) => {
  return (
    <div className="relative size-16 flex items-center justify-center border border-token-gray-300 rounded-full">
      <svg
        className={classNames(
          'absolute size-[calc(100%+2px)]',
          left ? undefined : styles.xchainAnimationDelayed,
        )}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 100 100"
        shapeRendering="geometricPrecision"
        textRendering="geometricPrecision"
      >
        <defs>
          <linearGradient
            id={`xchain-bot-${left ? 'l' : 'r'}`}
            x1="0"
            y1="0.634111"
            x2="0"
            y2="0.449339"
          >
            <stop offset="17%" stopColor={STROKE_COLOR} />
            <stop offset="97%" stopOpacity={0} stopColor={STROKE_COLOR} />
          </linearGradient>
          <linearGradient
            id={`xchain-top-${left ? 'l' : 'r'}`}
            x1="0"
            y1="0.634111"
            x2="0"
            y2="0.449339"
          >
            <stop offset="0%" stopOpacity={0} stopColor={STROKE_COLOR} />
            <stop offset="90%" stopColor={STROKE_COLOR} />
          </linearGradient>
        </defs>
        <g transform="translate(-70 -50)">
          <g mask={`url(#xchain-mask-bot-${left ? 'l' : 'r'})`}>
            <g transform="translate(120,100) rotate(22)">
              <circle
                className={styles['xchain-circle-bot']}
                r="49.5"
                transform="translate(0,0)"
                fill="none"
                fillOpacity="0"
                stroke={`url(#xchain-bot-${left ? 'l' : 'r'})`}
                strokeOpacity="0"
                strokeLinecap="round"
                strokeDashoffset="-140"
                strokeDasharray="30,311"
              />
            </g>
            <mask
              id={`xchain-mask-bot-${left ? 'l' : 'r'}`}
              x="-150%"
              y="-150%"
              height="400%"
              width="400%"
            >
              <rect
                width="124.617097"
                height="63.444782"
                rx="0"
                ry="0"
                transform="translate(56.555225 100)"
                fill="#d2dbed"
                strokeWidth="0"
              />
            </mask>
          </g>
          <g mask={`url(#xchain-mask-top-${left ? 'l' : 'r'})`}>
            <g transform="translate(120,100) rotate(-22)">
              <circle
                className={styles['xchain-circle-top']}
                r="49.5"
                transform="scale(1,-1) translate(0,0)"
                fill="none"
                fillOpacity="0"
                stroke={`url(#xchain-top-${left ? 'l' : 'r'})`}
                strokeOpacity="0"
                strokeLinecap="round"
                strokeDashoffset="-140"
                strokeDasharray="30,311"
              />
            </g>
            <mask
              id={`xchain-mask-top-${left ? 'l' : 'r'}`}
              x="-150%"
              y="-150%"
              height="400%"
              width="400%"
            >
              <rect
                width="124.096467"
                height="50"
                rx="0"
                ry="0"
                transform="translate(57.951767 50)"
                fill="#d2dbed"
                strokeWidth="0"
              />
            </mask>
          </g>
        </g>
      </svg>
      <TokenAvatar address={address} chainId={chainId} alt="" size={32} />
    </div>
  );
};

const ConnectorElement = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 47 4"
      width={45}
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <defs>
        <linearGradient id="line-1-fill" x1="1" y1="0.516705" x2="0" y2="0.5">
          <stop offset="0%" stopColor={STROKE_COLOR} />
          <stop offset="100%" stopColor={STROKE_COLOR} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path
        d="M169.5,100h47"
        transform="translate(-169.5 -98)"
        fill="none"
        stroke={STROKE_COLOR}
        strokeOpacity="0.06"
      />
      <g mask="url(#xchain-line-mask)">
        <g transform="translate(-17.244765 1.983575)">
          <rect
            className={styles['xchain-connector-line-1']}
            width="20"
            height="0.983211"
            rx="0.49"
            ry="0.49"
            transform="translate(-3.493558 -0.491605)"
            fill="url(#line-1-fill)"
            fillOpacity="0.3"
            strokeWidth="0"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <mask id="xchain-line-mask" x="-150%" y="-150%" height="400%" width="400%">
          <rect width="47" height="4" rx="0" ry="0" fill="#d2dbed" strokeWidth="0" />
        </mask>
      </g>
    </svg>
  );
};

/** Decorative sell → buy token animation for the pending screen. */
export const PendingTradeArt = ({
  sellToken,
  buyToken,
}: {
  sellToken: TokenInfo;
  buyToken: TokenInfo;
}) => (
  <div className="flex flex-row py-8 justify-center relative" aria-hidden="true">
    <TokenIconElement address={sellToken.address} chainId={sellToken.chainId} left />
    <div className="flex items-center">
      <ConnectorElement />
    </div>
    <TokenIconElement address={buyToken.address} chainId={buyToken.chainId} />
  </div>
);
